import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ClientsService } from "../services/clients";
import { selectUserRoles } from "../../SharedModule/redux/reducers/auth.reducer";
import {
  IsLoadedContacts,
  IsLoadingContacts,
  loadContacts,
  SelectActualClientId,
  SelectClientContacts,
  SelectContactsFilterText,
  SelectIsShowingActives,
  SelectNewUpdated,
  setIsLoadingContacts,
  setNewUpdate,
} from "../redux/reducers/clientContacts.reducer";
import { addToast } from "../../SharedModule/redux/reducers/ui.reducer";
import { useNavigate } from "react-router";
import {
  ROUTE_CLIENTS,
  ROUTE_FORBIDDEN_PAGE,
} from "../../SharedModule/utils/constants";
import {
  IsClientsLoaded,
  SelectActualClientForHeaderInfo,
  SelectClientsIds,
  setActualClientForHeaderInfo,
  setIsActualClientForHeaderInfoLoading,
} from "../redux/reducers/clientsList.reducer";
import { Client } from "../types/clients-module-types";
import { useClientsList } from "./useClientsList";

export const useClientContacts = (clientId: number) => {
  useClientsList();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { userRolesList, rolesLoaded } = useSelector(selectUserRoles);
  const isLoadingContacts = useSelector(IsLoadingContacts);
  const isLoadedContacts = useSelector(IsLoadedContacts);
  const clientContactsInfo = useSelector(SelectClientContacts);
  const actualClientId = useSelector(SelectActualClientId);
  const isShowingActives = useSelector(SelectIsShowingActives);
  const contactsFilterText = useSelector(SelectContactsFilterText);
  const newUpdated = useSelector(SelectNewUpdated);

  // To check is header info is already loaded
  const ClientForHeaderInfo: Client = useSelector(
    SelectActualClientForHeaderInfo
  );
  // to check if clientId is in list of clients that user could see
  const isClientsLoaded = useSelector(IsClientsLoaded);
  const clientsIds = useSelector(SelectClientsIds);

  // Tracks which fetch blocks are in flight, and (in doneRef) which clientId
  // each block last completed for. The callback is async and not awaited by
  // the effect, so a block's dispatch can change a dependency and re-run the
  // effect while an earlier request is still pending. A second, overlapping
  // invocation is a *different* closure — its own `isLoadedContacts`/
  // `actualClientId` reads are frozen at whatever they were when THAT closure
  // was created, so an in-flight-only guard isn't enough: a stale invocation
  // can still see them as not-yet-loaded right after a newer invocation has
  // already finished, and re-fire. doneRef is a plain mutable ref, so every
  // invocation (stale or not) reads its truly-current value.
  const inFlightRef = useRef<Record<string, boolean>>({});
  const doneRef = useRef<Record<string, number>>({});

  const loadClientContacts = useCallback(
    async (clientId) => {
      if (rolesLoaded && isClientsLoaded) {
        // This check should go in all clients custom hooks
        if (!clientsIds.includes(clientId)) {
          dispatch(
            addToast({
              mode: "error",
              message: "You're not allowed to see this Client Page",
            })
          );
          navigate(ROUTE_FORBIDDEN_PAGE);
          return true;
        }
        // Load info header when user put a new URL
        if (
          !inFlightRef.current.header &&
          doneRef.current.header !== clientId &&
          (!ClientForHeaderInfo ||
            (ClientForHeaderInfo && ClientForHeaderInfo.id !== clientId))
        ) {
          inFlightRef.current.header = true;
          dispatch(setIsActualClientForHeaderInfoLoading(true));
          let headerInfo = await ClientsService.getClientModuleHeaderInfo(
            clientId
          )
            .then((response) => {
              if (!response.success) {
                dispatch(
                  addToast({
                    mode: "error",
                    message: response.message,
                  })
                );
                dispatch(setIsActualClientForHeaderInfoLoading(false));
                navigate(ROUTE_CLIENTS);
                return true;
              } else {
                return response.data;
              }
            })
            .catch(() => {
              dispatch(
                addToast({
                  mode: "error",
                  message:
                    "Something wrong happened while getting the client header information.",
                })
              );
              dispatch(setIsActualClientForHeaderInfoLoading(false));
              navigate(ROUTE_CLIENTS);
              return true;
            });
          inFlightRef.current.header = false;
          doneRef.current.header = clientId;
          dispatch(setActualClientForHeaderInfo(headerInfo));
        }
        // page info
        if (
          !inFlightRef.current.contacts &&
          (newUpdated || doneRef.current.contacts !== clientId) &&
          (!isLoadedContacts ||
            !actualClientId ||
            actualClientId !== clientId ||
            newUpdated)
        ) {
          inFlightRef.current.contacts = true;
          dispatch(setIsLoadingContacts(true));
          const clientsContactsInfo = await ClientsService.getClientContacts(
            clientId
          )
            .then((data) => {
              if (!data.success) {
                dispatch(
                  addToast({
                    mode: "error",
                    message: "Contact not found",
                  })
                );
                dispatch(setIsLoadingContacts(false));
                navigate(ROUTE_CLIENTS);
                return true;
              } else {
                dispatch(setNewUpdate(false));
                return data;
              }
            })
            .catch(() => {
              dispatch(
                addToast({
                  mode: "error",
                  message: "Contact not found",
                })
              );
              dispatch(setIsLoadingContacts(false));
              navigate(ROUTE_CLIENTS);
              return true;
            });
          inFlightRef.current.contacts = false;
          doneRef.current.contacts = clientId;
          dispatch(loadContacts(clientsContactsInfo));
        }
      }
    },
    [dispatch, rolesLoaded, isClientsLoaded, isLoadedContacts, actualClientId, newUpdated]
  );

  useEffect(() => {
    loadClientContacts(clientId);
  }, [loadClientContacts]);

  return {
    ClientForHeaderInfo,
    isLoadingContacts,
    clientContactsInfo,
    userRolesList,
    rolesLoaded,
    isShowingActives,
    contactsFilterText,
  };
};
